import * as React from 'react';
import { Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import apiClient, { getErrorMessage, shouldRedirectToLogin } from '../services/api-client';
import AuthService from '../services/auth';
import CustomSnackbar from '../components/CustomSnackbar';
import LocationPickerMap from '../components/LocationPickerMap';
import { orgLogoUrl } from '../components/OrgLogo';
import { analyzeLogo, generateMonotoneVariant, downscaleImage } from '../utils/logoThemeVariants';
import GestionTypeIcon from '../components/GestionTypeIcon';
import SolarIcon from '../icons/SolarIcon';
import { isReservedTeam } from '../services/systemTeams';

// Module icons use the flat, single-color Solar Linear icon set so the grid
// stays visually consistent (no emoji). Icons inherit currentColor.
const ALL_MODULES = [
  { key: 'visits', label: 'Gestión de Visitas', icon: 'users-group-rounded' },
  { key: 'amenities', label: 'Amenidades', icon: 'swimming' },
  { key: 'issues', label: 'Reportes de Averías', icon: 'shield-warning' },
  { key: 'parksharing', label: 'Parqueos Compartidos', icon: 'garage' },
  { key: 'reporting', label: 'Reportería', icon: 'chart' },
  { key: 'directory', label: 'Directorio', icon: 'notebook' },
  { key: 'airbnb', label: 'Airbnb', icon: 'bed' },
  { key: 'paqueteria', label: 'Paquetería', icon: 'box' },
];

const NOTIFICATION_MODES = [
  { value: 'mail', label: 'Email' },
  { value: 'push', label: 'Push Notification' },
  { value: 'both', label: 'Ambos' },
];

const GESTION_TYPE_FIELD_LABELS = {
  nombre: 'Nombre',
  nombre_plural: 'Nombre Plural',
  descripcion: 'Descripción',
  id_prefix: 'Prefijo del Ticket',
  creadores_permitidos: 'Creadores Permitidos',
  unassigned_rw_teams: 'Equipo(s) para Gestionar Nuevos Tickets',
};

const GESTION_TYPE_CREATOR_FIXED_OPTIONS = [
  { value: 'habitante', label: 'Habitante' },
  { value: 'receptor', label: 'Receptor' },
  { value: 'admin', label: 'Administrador' },
];

const UNIT_TYPES = [
  { value: 'Casa', label: 'Casa' },
  { value: 'Apartamento', label: 'Apartamento' },
];

const TABS = [
  { label: 'Contacto y Notificaciones', icon: <NotificationsOutlinedIcon fontSize="small" /> },
  { label: 'Módulos', icon: <ViewModuleOutlinedIcon fontSize="small" /> },
  { label: 'Configuración Adicional', icon: <TuneOutlinedIcon fontSize="small" /> },
  { label: 'Equipos & Gestiones', icon: <GroupsOutlinedIcon fontSize="small" /> },
];

// RBAC cell states cycle: none -> read -> write -> none
const RBAC_STATES = ['none', 'read', 'write'];

function rbacCellState(matrix, typeKey, teamKey) {
  const entry = matrix?.[typeKey]?.[teamKey];
  if (!entry) { return 'none'; }
  if (entry.write) { return 'write'; }
  if (entry.read) { return 'read'; }
  return 'none';
}

function rbacNextState(current) {
  const idx = RBAC_STATES.indexOf(current);
  return RBAC_STATES[(idx + 1) % RBAC_STATES.length];
}

function rbacEntryForState(state) {
  if (state === 'read') { return { read: true, write: false }; }
  if (state === 'write') { return { read: true, write: true }; }
  return { read: false, write: false };
}

const DEFAULT_AIRBNB_CONFIG = {
  id_number: {
    ask: false,
    required: false,
  },
  id_picture: {
    ask: true,
    required: true,
  },
  languages: ['es', 'en', 'pt'],
};

const DEFAULT_RULESET = {
  es: {
    title: 'Reglamento para Huéspedes',
    rules: [
      'Está prohibido generar ruidos o sonidos molestos, como televisores o equipos de sonido con volumen alto, que puedan molestar a otros ocupantes del edificio. Estos dispositivos deben mantenerse a un nivel moderado.',
      'Se debe respetar la tranquilidad y el orden dentro de las instalaciones, por lo que no se permite la realización de fiestas ni eventos sociales en ninguna parte de la propiedad.',
      'Las amenidades del edificio requieren reservación previa, por favor consultar en recepción para más información de cómo utilizarlas.',
      'Es prohibido fumar en las instalaciones del edificio, adentro de los apartamentos y en las áreas comunes.',
      'No se permite el ingreso de huéspedes externos a la reservación de Airbnb.',
      'En caso de no cumplir con las reglas, la administración tendrá la obligación de advertirte, y si no se acatan las advertencias, tendrá la potestad de dar por terminada la estancia y solicitar que abandones las instalaciones.',
    ],
  },
};

const DEFAULT_VISIT_RULES = {
  default: {
    vehicle: true,
    walking: true,
    default_lifespan_hours: 24,
    allow_multiple_entries: true,
    allow_longer_lifespan: true,
    max_lifespan_hours: 168,
  },
};

function buildNormalizedAirbnbConfig(config) {
  const nextConfig = JSON.parse(JSON.stringify(DEFAULT_AIRBNB_CONFIG));
  if (config && typeof config === 'object') {
    nextConfig.id_number.ask = Boolean(config.id_number?.ask);
    nextConfig.id_number.required = Boolean(config.id_number?.required);
    nextConfig.id_picture.ask = config.id_picture?.ask !== undefined ? Boolean(config.id_picture.ask) : true;
    nextConfig.id_picture.required = config.id_picture?.required !== undefined ? Boolean(config.id_picture.required) : true;
    if (Array.isArray(config.languages)) {
      const validLanguages = config.languages.filter((language, index) => ['es', 'en', 'pt'].includes(language) && config.languages.indexOf(language) === index);
      nextConfig.languages = ['es', 'en', ...(validLanguages.includes('pt') ? ['pt'] : [])];
    }
  }
  if (!nextConfig.id_number.ask) {
    nextConfig.id_number.required = false;
  }
  if (!nextConfig.id_picture.ask) {
    nextConfig.id_picture.required = false;
  }
  return nextConfig;
}

function buildNormalizedRuleset(ruleset, airbnbConfig) {
  const normalizedRuleset = JSON.parse(JSON.stringify(DEFAULT_RULESET));
  if (ruleset && typeof ruleset === 'object') {
    Object.entries(ruleset).forEach(([language, block]) => {
      if (!block || typeof block !== 'object') {
        return;
      }
      normalizedRuleset[language] = {
        title: String(block.title || '').trim(),
        rules: Array.isArray(block.rules) ? block.rules.map((rule) => String(rule).trim()).filter(Boolean) : [],
      };
    });
  }
  if (!normalizedRuleset.es?.title) {
    normalizedRuleset.es = JSON.parse(JSON.stringify(DEFAULT_RULESET.es));
  }
  if (!Array.isArray(normalizedRuleset.es?.rules) || normalizedRuleset.es.rules.length === 0) {
    normalizedRuleset.es = JSON.parse(JSON.stringify(DEFAULT_RULESET.es));
  }
  Object.keys(normalizedRuleset).forEach((language) => {
    if (language === 'es') {
      return;
    }
    const block = normalizedRuleset[language];
    if (!block?.title || !Array.isArray(block.rules) || block.rules.length === 0) {
      delete normalizedRuleset[language];
    }
  });
  if (!airbnbConfig?.languages?.includes('pt')) {
    delete normalizedRuleset.pt;
  }
  return normalizedRuleset;
}

function buildNormalizedVisitRules(rules) {
  const normalizedRules = JSON.parse(JSON.stringify(DEFAULT_VISIT_RULES));
  const source = rules?.default && typeof rules.default === 'object' ? rules.default : {};
  const defaultLifespanHours = Number(source.default_lifespan_hours);
  const maxLifespanHours = Number(source.max_lifespan_hours);
  normalizedRules.default.vehicle = source.vehicle !== undefined ? Boolean(source.vehicle) : true;
  normalizedRules.default.walking = source.walking !== undefined ? Boolean(source.walking) : true;
  normalizedRules.default.default_lifespan_hours = Number.isFinite(defaultLifespanHours) && defaultLifespanHours > 0 ? Math.round(defaultLifespanHours) : 24;
  normalizedRules.default.allow_multiple_entries = source.allow_multiple_entries !== undefined ? Boolean(source.allow_multiple_entries) : true;
  normalizedRules.default.allow_longer_lifespan = source.allow_longer_lifespan !== undefined ? Boolean(source.allow_longer_lifespan) : true;
  normalizedRules.default.max_lifespan_hours = Number.isFinite(maxLifespanHours) && maxLifespanHours > 0 ? Math.round(maxLifespanHours) : 168;
  if (normalizedRules.default.max_lifespan_hours < normalizedRules.default.default_lifespan_hours) {
    normalizedRules.default.max_lifespan_hours = normalizedRules.default.default_lifespan_hours;
  }
  return normalizedRules;
}

function normalizeDashboardConfig(config) {
  if (!config) {
    return config;
  }
  const nextConfig = JSON.parse(JSON.stringify(config));
  nextConfig.airbnb_config = buildNormalizedAirbnbConfig(config.airbnb_config);
  nextConfig.ruleset = buildNormalizedRuleset(config.ruleset, nextConfig.airbnb_config);
  nextConfig.rules = buildNormalizedVisitRules(config.rules);
  if (!['mail', 'push', 'both'].includes(nextConfig.reservation_notification_mode)) {
    nextConfig.reservation_notification_mode = 'push';
  }
  const mt = config.multi_tower;
  nextConfig.multi_tower = {
    enabled: Boolean(mt && typeof mt === 'object' && mt.enabled),
    towers: mt && typeof mt === 'object' && Array.isArray(mt.towers)
      ? mt.towers.filter((t) => typeof t === 'string' && t.length > 0)
      : [],
  };
  return nextConfig;
}

function RulesEditor({ title, value, onChange, onAddRule, onRemoveRule }) {
  return (
    <Stack spacing={2}>
      <TextField
        label="Título"
        value={value?.title || ''}
        onChange={(event) => onChange('title', event.target.value)}
        fullWidth
      />
      <Stack spacing={1.5}>
        {(value?.rules || []).map((rule, index) => (
          <Stack key={`rule-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label={`Regla ${index + 1}`}
              value={rule}
              onChange={(event) => onChange(`rules.${index}`, event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Button color="error" onClick={() => onRemoveRule(index)} sx={{ minWidth: { sm: 120 } }}>
              Eliminar
            </Button>
          </Stack>
        ))}
        <Button variant="outlined" color="secondary" onClick={onAddRule} sx={{ alignSelf: 'flex-start' }}>
          Agregar regla
        </Button>
      </Stack>
    </Stack>
  );
}

function MailingListField({ label, list, onAdd, onRemove }) {
  const [input, setInput] = React.useState('');

  const handleAdd = React.useCallback(() => {
    const value = input.trim();
    if (!value || !value.includes('@')) {
      return;
    }
    onAdd(value);
    setInput('');
  }, [input, onAdd]);

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>{label}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={input}
          label="Correo"
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button variant="contained" color="secondary" onClick={handleAdd} sx={{ minWidth: { sm: 120 } }}>
          Agregar
        </Button>
      </Stack>
      {list.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {list.map((email, index) => (
            <Chip key={`${email}-${index}`} label={email} onDelete={() => onRemove(index)} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function TowerListField({ list, onAdd, onRemove }) {
  const [input, setInput] = React.useState('');
  const [error, setError] = React.useState('');

  const handleAdd = React.useCallback(() => {
    const value = input.trim();
    if (!value || value.includes('-') || /\s/.test(value) || list.includes(value)) {
      setError('Nombre inválido o duplicado. Sin guiones ni espacios.');
      return;
    }
    onAdd(value);
    setInput('');
    setError('');
  }, [input, list, onAdd]);

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Torres</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          value={input}
          label="Nombre de torre"
          error={Boolean(error)}
          helperText={error}
          onChange={(event) => {
            setInput(event.target.value);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button variant="contained" color="secondary" onClick={handleAdd} sx={{ minWidth: { sm: 120 } }}>
          Agregar
        </Button>
      </Stack>
      {list.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {list.map((tower, index) => (
            <Chip key={`${tower}-${index}`} label={tower} onDelete={() => onRemove(index)} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default function AdminDashboard({ setNavbarState, navbarState }) {
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState(0);
  const [config, setConfig] = React.useState(null);
  const [error, setError] = React.useState('');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [settingsScreen, setSettingsScreen] = React.useState(null);
  const [rulesetBusy, setRulesetBusy] = React.useState(false);
  const [planCatalog, setPlanCatalog] = React.useState([]);
  const [issuesConfig, setIssuesConfig] = React.useState(null);
  const [issuesConfigLoading, setIssuesConfigLoading] = React.useState(false);
  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState(null);
  const [visitTypeDraft, setVisitTypeDraft] = React.useState('');
  const [logoAnalysis, setLogoAnalysis] = React.useState(null);
  const [logoLight, setLogoLight] = React.useState(null);
  const [logoDark, setLogoDark] = React.useState(null);
  const [logoProcessing, setLogoProcessing] = React.useState(false);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoVersion, setLogoVersion] = React.useState(0);
  const [rbacMatrix, setRbacMatrix] = React.useState(null);
  const [rbacTypes, setRbacTypes] = React.useState([]);
  const [rbacTeams, setRbacTeams] = React.useState([]);
  const [rbacLoading, setRbacLoading] = React.useState(false);
  const [rbacDirty, setRbacDirty] = React.useState(false);
  const [rbacSaving, setRbacSaving] = React.useState(false);
  const [gestionTypes, setGestionTypes] = React.useState([]);
  const [gestionTypeDraft, setGestionTypeDraft] = React.useState(null);
  const [gestionTypeSaving, setGestionTypeSaving] = React.useState(false);
  const [teams, setTeams] = React.useState({});
  const orgKey = AuthService.getCurrentUserOrgKey();
  const saveTimeoutRef = React.useRef(null);

  const handleOpenAirbnbSettings = React.useCallback(() => {
    setActiveTab(1);
    setSettingsScreen('airbnb');
  }, []);

  const handleCloseAirbnbSettings = React.useCallback(() => {
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const handleOpenVisitSettings = React.useCallback(() => {
    setActiveTab(1);
    setSettingsScreen('visits');
  }, []);

  const handleCloseVisitSettings = React.useCallback(() => {
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const handleOpenIssuesSettings = React.useCallback(() => {
    setActiveTab(1);
    setSettingsScreen('issues');
  }, []);

  const handleCloseIssuesSettings = React.useCallback(() => {
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const handleOpenGestionTypeSettings = React.useCallback((typeKey) => {
    const gtype = gestionTypes.find((t) => t.key === typeKey);
    if (!gtype) { return; }
    setGestionTypeDraft(JSON.parse(JSON.stringify(gtype)));
    setActiveTab(1);
    setSettingsScreen(`gestion_type_${typeKey}`);
  }, [gestionTypes]);

  const handleCloseGestionTypeSettings = React.useCallback(() => {
    setGestionTypeDraft(null);
    setSettingsScreen(null);
    setActiveTab(1);
  }, []);

  const fetchPlanCatalog = React.useCallback(async () => {
    try {
      const response = await apiClient.get('plans', { parseAppConfig: false }, { action: 'fetch_plan_catalog' });
      if (response.status === 200 && Array.isArray(response.data?.data?.plans)) {
        setPlanCatalog(response.data.data.plans);
      } else {
        setPlanCatalog([]);
      }
    } catch (err) {
      console.error('Failed to load plan catalog', err);
      setPlanCatalog([]);
    }
  }, []);

  const fetchConfig = React.useCallback(async () => {
    if (!orgKey) {
      setLoading(false);
      setError('No se encontró la organización del administrador.');
      return;
    }
    try {
      const response = await apiClient.get(`landing/dashboard/${orgKey}`, {}, { action: 'fetch_admin_dashboard_config' });
      if (response.status === 200) {
        setConfig(normalizeDashboardConfig(response.data.data));
        setError('');
      }
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setError(getErrorMessage(fetchError, 'No se pudo cargar la configuración.'));
    } finally {
      setLoading(false);
    }
  }, [orgKey]);

  const fetchIssuesConfig = React.useCallback(async () => {
    if (!orgKey) { return; }
    setIssuesConfigLoading(true);
    try {
      const res = await apiClient.get('issues/config', {}, { action: 'fetch_issues_config' });
      if (res.status === 200) {
        setIssuesConfig(res.data.data);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo cargar la configuración de averías.'));
      setSnackbarOpen(true);
    } finally {
      setIssuesConfigLoading(false);
    }
  }, [orgKey]);

  const fetchRbacMatrix = React.useCallback(async () => {
    if (!orgKey) { return; }
    setRbacLoading(true);
    try {
      const res = await apiClient.get('gestiones/rbac', {}, { action: 'fetch_gestiones_rbac' });
      if (res.status === 200) {
        const payload = res.data.data || {};
        setRbacMatrix(payload.rbac || {});
        setRbacTypes(Array.isArray(payload.types) ? payload.types : []);
        setRbacTeams(Array.isArray(payload.teams) ? payload.teams : []);
        setRbacDirty(false);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo cargar la matriz de permisos.'));
      setSnackbarOpen(true);
    } finally {
      setRbacLoading(false);
    }
  }, [orgKey]);

  const performRbacSave = React.useCallback(async (matrixToSave) => {
    if (!orgKey) { return; }
    setRbacSaving(true);
    try {
      const res = await apiClient.put('gestiones/rbac', { rbac: matrixToSave }, {}, { action: 'save_gestiones_rbac' });
      if (res.status === 200) {
        const saved = (res.data.data && res.data.data.rbac) || matrixToSave;
        setRbacMatrix(saved);
        setRbacDirty(false);
        setSnackbarSeverity('success');
        setSnackbarMessage('Matriz de permisos guardada.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo guardar la matriz de permisos.'));
      setSnackbarOpen(true);
    } finally {
      setRbacSaving(false);
    }
  }, [orgKey]);

  const fetchGestionTypes = React.useCallback(async () => {
    try {
      const res = await apiClient.get('gestiones/types', { parseAppConfig: false }, { action: 'fetch_gestion_types' });
      if (res.status === 200 && Array.isArray(res.data?.data)) {
        setGestionTypes(res.data.data);
      } else if (res.status === 200 && Array.isArray(res.data?.data?.types)) {
        setGestionTypes(res.data.data.types);
      }
    } catch (err) {
      // Non-fatal: gestion types are optional. Stay silent on 404/403.
      console.debug('Failed to load gestion types', err);
    }
  }, []);

  const fetchTeams = React.useCallback(async () => {
    try {
      const res = await apiClient.get('teams', {}, { action: 'fetch_teams' });
      if (res.status === 200 && res.data?.data) {
        setTeams(res.data.data.teams || {});
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      console.debug('Failed to load teams', err);
    }
  }, []);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    fetchPlanCatalog();
    fetchConfig();
    fetchGestionTypes();
    fetchTeams();
  }, [fetchConfig, fetchPlanCatalog, fetchGestionTypes, fetchTeams]);

  React.useEffect(() => {
    if (settingsScreen === 'issues' && !issuesConfig && !issuesConfigLoading) {
      fetchIssuesConfig();
    }
  }, [settingsScreen, issuesConfig, issuesConfigLoading, fetchIssuesConfig]);

  React.useEffect(() => {
    if (activeTab === 3 && rbacMatrix === null && !rbacLoading) {
      fetchRbacMatrix();
    }
  }, [activeTab, rbacMatrix, rbacLoading, fetchRbacMatrix]);

  const updateConfig = React.useCallback((path, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prevConfig));
      let current = updated;
      for (let index = 0; index < keys.length - 1; index += 1) {
        current[keys[index]] = { ...(current[keys[index]] || {}) };
        current = current[keys[index]];
      }
      current[keys[keys.length - 1]] = value;
      updated.airbnb_config = buildNormalizedAirbnbConfig(updated.airbnb_config);
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      updated.rules = buildNormalizedVisitRules(updated.rules);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const updateRuleset = React.useCallback((language, path, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      if (!updated.ruleset?.[language]) {
        updated.ruleset = updated.ruleset || {};
        updated.ruleset[language] = { title: '', rules: [] };
      }
      const keys = path.split('.');
      let current = updated.ruleset[language];
      for (let index = 0; index < keys.length - 1; index += 1) {
        const key = keys[index];
        if (Array.isArray(current)) {
          current = current[Number(key)];
        } else {
          current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...(current[key] || {}) };
          current = current[key];
        }
      }
      current[keys[keys.length - 1]] = value;
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const selectedPlan = React.useMemo(() => {
    if (!config) {
      return null;
    }
    const planId = (config.subscription && config.subscription.plan) || config.plan;
    if (!planId) {
      return null;
    }
    return planCatalog.find((plan) => plan.id === planId) || null;
  }, [config, planCatalog]);

  const planChipLabel = React.useMemo(() => {
    if (selectedPlan) {
      const cycle = selectedPlan.billing_cycle || 'mes';
      return `${selectedPlan.name} (${selectedPlan.currency}${selectedPlan.price}/${cycle})`;
    }
    if (config?.plan) {
      return `Plan ${String(config.plan).toUpperCase()}`;
    }
    return null;
  }, [config?.plan, selectedPlan]);

  const addAirbnbRule = React.useCallback((language) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      const currentRules = Array.isArray(updated.ruleset?.[language]?.rules) ? [...updated.ruleset[language].rules] : [];
      currentRules.push('');
      updated.ruleset = updated.ruleset || {};
      updated.ruleset[language] = {
        title: updated.ruleset?.[language]?.title || '',
        rules: currentRules,
      };
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeAirbnbRule = React.useCallback((language, index) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      const currentRules = Array.isArray(updated.ruleset?.[language]?.rules) ? [...updated.ruleset[language].rules] : [];
      currentRules.splice(index, 1);
      updated.ruleset[language] = {
        title: updated.ruleset?.[language]?.title || '',
        rules: currentRules,
      };
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const setPortugueseEnabled = React.useCallback((enabled) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const updated = JSON.parse(JSON.stringify(prevConfig));
      updated.airbnb_config = buildNormalizedAirbnbConfig({
        ...updated.airbnb_config,
        languages: enabled ? ['es', 'en', 'pt'] : ['es', 'en'],
      });
      updated.ruleset = buildNormalizedRuleset(updated.ruleset, updated.airbnb_config);
      return updated;
    });
    setHasUnsavedChanges(true);
  }, []);

  const generateRuleset = React.useCallback(async (mode) => {
    if (!config || !orgKey) {
      return;
    }
    setRulesetBusy(true);
    try {
      const response = await apiClient.post(
        `landing/dashboard/${orgKey}/airbnb-ruleset/generate`,
        {
          mode,
          languages: config.airbnb_config?.languages?.includes('pt') ? ['en', 'pt'] : ['en'],
          airbnb_config: config.airbnb_config,
          ruleset: config.ruleset,
        },
        {},
        { action: 'generate_airbnb_ruleset' }
      );
      if (response.status === 200) {
        setConfig((prevConfig) => normalizeDashboardConfig({ ...prevConfig, ruleset: response.data.data }));
        setHasUnsavedChanges(true);
        setSnackbarSeverity('success');
        setSnackbarMessage(mode === 'missing' ? 'Idiomas faltantes generados.' : 'Idiomas regenerados correctamente.');
        setSnackbarOpen(true);
      }
    } catch (generationError) {
      if (shouldRedirectToLogin(generationError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(generationError, 'No se pudieron generar los idiomas.'));
      setSnackbarOpen(true);
    } finally {
      setRulesetBusy(false);
    }
  }, [config, orgKey]);

  const addToList = React.useCallback((field, value) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const currentList = Array.isArray(prevConfig[field]) ? [...prevConfig[field]] : [];
      if (currentList.includes(value)) {
        return prevConfig;
      }
      return { ...prevConfig, [field]: [...currentList, value] };
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeFromList = React.useCallback((field, index) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const currentList = Array.isArray(prevConfig[field]) ? [...prevConfig[field]] : [];
      currentList.splice(index, 1);
      return { ...prevConfig, [field]: currentList };
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleLogoChange = React.useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
    setLogoProcessing(true);
    setLogoAnalysis(null);
    setLogoLight(null);
    setLogoDark(null);
    try {
      const analysis = await analyzeLogo(file);
      setLogoAnalysis(analysis);
      if (analysis.conflictsLight) {
        const lightVariant = await generateMonotoneVariant(file, '#021545');
        setLogoLight(lightVariant);
      }
      if (analysis.conflictsDark) {
        const darkVariant = await generateMonotoneVariant(file, '#f8fafc');
        setLogoDark(darkVariant);
      }
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('No se pudo analizar el logo.');
      setSnackbarOpen(true);
    } finally {
      setLogoProcessing(false);
    }
  }, []);

  const handleLogoUpload = React.useCallback(async () => {
    if (!logoFile || !orgKey) return;
    setLogoUploading(true);
    try {
      const scaled = await downscaleImage(logoFile, 512);
      const formData = new FormData();
      formData.append('logo', scaled);
      if (logoLight) formData.append('logo_light', logoLight);
      if (logoDark) formData.append('logo_dark', logoDark);
      const res = await apiClient.post(`landing/dashboard/${orgKey}/logo`, formData, {}, { action: 'upload_org_logo' });
      if (res.status === 200) {
        setSnackbarSeverity('success');
        setSnackbarMessage('Logo actualizado correctamente.');
        setSnackbarOpen(true);
        setLogoFile(null);
        setLogoPreview(null);
        setLogoAnalysis(null);
        setLogoLight(null);
        setLogoDark(null);
        setLogoVersion((v) => v + 1);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo actualizar el logo.'));
      setSnackbarOpen(true);
    } finally {
      setLogoUploading(false);
    }
  }, [logoFile, logoLight, logoDark, orgKey]);

  const performSave = React.useCallback(async (configToSave) => {
    if (!configToSave || !orgKey) {
      return;
    }
    try {
      const body = { ...configToSave };
      const mt = body.multi_tower;
      const strippedMt = mt && mt.enabled && (!Array.isArray(mt.towers) || mt.towers.length === 0) ? mt : null;
      if (strippedMt) {
        delete body.multi_tower;
      }
      const response = await apiClient.put(`landing/dashboard/${orgKey}`, body, {}, { action: 'save_admin_dashboard_config' });
      if (response.status === 200) {
        const saved = normalizeDashboardConfig(response.data.data || configToSave);
        if (strippedMt) {
          saved.multi_tower = { enabled: strippedMt.enabled, towers: [...strippedMt.towers || []] };
        }
        setConfig(saved);
        setSnackbarSeverity('success');
        setSnackbarMessage('Configuración guardada exitosamente.');
        setSnackbarOpen(true);
        setHasUnsavedChanges(false);
      }
    } catch (saveError) {
      if (shouldRedirectToLogin(saveError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(saveError, 'No se pudo guardar la configuración.'));
      setSnackbarOpen(true);
    }
  }, [orgKey]);

  const performIssuesConfigSave = React.useCallback(async (configToSave) => {
    if (!configToSave || !orgKey) { return; }
    try {
      const res = await apiClient.put('issues/config', configToSave, {}, { action: 'save_issues_config' });
      if (res.status === 200) {
        setIssuesConfig(res.data.data);
        setSnackbarSeverity('success');
        setSnackbarMessage('Configuración de averías guardada.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo guardar la configuración de averías.'));
      setSnackbarOpen(true);
    }
  }, [orgKey]);

  const issuesSaveTimeoutRef = React.useRef(null);
  const [issuesUnsaved, setIssuesUnsaved] = React.useState(false);

  const updateIssuesConfig = React.useCallback((path, value) => {
    setIssuesConfig((prev) => {
      if (!prev) { return prev; }
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prev));
      let current = updated;
      for (let i = 0; i < keys.length - 1; i += 1) {
        current[keys[i]] = { ...(current[keys[i]] || {}) };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  React.useEffect(() => {
    if (issuesUnsaved && issuesConfig) {
      if (issuesSaveTimeoutRef.current) { clearTimeout(issuesSaveTimeoutRef.current); }
      issuesSaveTimeoutRef.current = setTimeout(() => {
        performIssuesConfigSave(issuesConfig);
        setIssuesUnsaved(false);
      }, 600);
    }
    return () => {
      if (issuesSaveTimeoutRef.current) { clearTimeout(issuesSaveTimeoutRef.current); }
    };
  }, [issuesConfig, issuesUnsaved, performIssuesConfigSave]);

  const updateIssueStateField = React.useCallback((stateKey, field, value) => {
    setIssuesConfig((prev) => {
      if (!prev || !prev.issues_states) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.issues_states[stateKey]) { return prev; }
      updated.issues_states[stateKey][field] = value;
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  const toggleIssueStateTransition = React.useCallback((stateKey, target, enabled) => {
    setIssuesConfig((prev) => {
      if (!prev || !prev.issues_states) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      const entry = updated.issues_states[stateKey];
      if (!entry) { return prev; }
      const list = Array.isArray(entry.siguiente_estado) ? [...entry.siguiente_estado] : [];
      if (enabled && !list.includes(target)) { list.push(target); }
      if (!enabled) { const i = list.indexOf(target); if (i >= 0) { list.splice(i, 1); } }
      updated.issues_states[stateKey].siguiente_estado = list;
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  const addIssueState = React.useCallback(() => {
    setIssuesConfig((prev) => {
      if (!prev || !prev.issues_states) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      let n = 1;
      let newKey = `nuevo_estado_${n}`;
      while (updated.issues_states[newKey]) { n += 1; newKey = `nuevo_estado_${n}`; }
      updated.issues_states[newKey] = { nombre_largo: 'Nuevo estado', nombre_corto: 'Nuevo', nombre_accion: 'Nuevo', siguiente_estado: [] };
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  const removeIssueState = React.useCallback((stateKey) => {
    setIssuesConfig((prev) => {
      if (!prev || !prev.issues_states) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      delete updated.issues_states[stateKey];
      // Remove references to the deleted state from other states' siguiente_estado
      Object.values(updated.issues_states).forEach((entry) => {
        if (Array.isArray(entry.siguiente_estado)) {
          entry.siguiente_estado = entry.siguiente_estado.filter((s) => s !== stateKey);
        }
      });
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  const renameIssueStateKey = React.useCallback((oldKey, newKey) => {
    setIssuesConfig((prev) => {
      if (!prev || !prev.issues_states) { return prev; }
      const trimmed = String(newKey).trim();
      if (!trimmed || trimmed === oldKey) { return prev; }
      if (prev.issues_states[trimmed]) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      const entry = updated.issues_states[oldKey];
      delete updated.issues_states[oldKey];
      updated.issues_states[trimmed] = entry;
      // Update references in other states
      Object.values(updated.issues_states).forEach((e) => {
        if (Array.isArray(e.siguiente_estado)) {
          e.siguiente_estado = e.siguiente_estado.map((s) => (s === oldKey ? trimmed : s));
        }
      });
      return updated;
    });
    setIssuesUnsaved(true);
  }, []);

  const updateGestionTypeDraft = React.useCallback((field, value) => {
    setGestionTypeDraft((prev) => {
      if (!prev) { return prev; }
      const updated = JSON.parse(JSON.stringify(prev));
      updated[field] = value;
      return updated;
    });
  }, []);

  const performGestionTypeSave = React.useCallback(async () => {
    if (!gestionTypeDraft) { return; }
    const typeKey = gestionTypeDraft.key;
    const orgControlled = Array.isArray(gestionTypeDraft.org_controlled) ? gestionTypeDraft.org_controlled : [];
    const payload = {};
    for (const field of orgControlled) {
      if (Object.prototype.hasOwnProperty.call(gestionTypeDraft, field)) {
        payload[field] = gestionTypeDraft[field];
      }
    }
    setGestionTypeSaving(true);
    try {
      const res = await apiClient.put(`gestiones/type/${typeKey}`, payload, {}, { action: 'save_gestion_type' });
      if (res.status === 200) {
        const savedType = (res.data && res.data.data && res.data.data.type) || (res.data && res.data.data) || gestionTypeDraft;
        setGestionTypes((prev) => prev.map((t) => (t.key === typeKey ? { ...t, ...savedType } : t)));
        setGestionTypeDraft(savedType);
        setSnackbarSeverity('success');
        setSnackbarMessage('Configuración de gestión guardada.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      if (shouldRedirectToLogin(err)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbarSeverity('error');
      setSnackbarMessage(getErrorMessage(err, 'No se pudo guardar la configuración de gestión.'));
      setSnackbarOpen(true);
    } finally {
      setGestionTypeSaving(false);
    }
  }, [gestionTypeDraft]);

  const cycleRbacCell = React.useCallback((typeKey, teamKey) => {
    setRbacMatrix((prev) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const current = rbacCellState(next, typeKey, teamKey);
      const newState = rbacNextState(current);
      if (!next[typeKey]) { next[typeKey] = {}; }
      next[typeKey][teamKey] = rbacEntryForState(newState);
      return next;
    });
    setRbacDirty(true);
  }, []);

  React.useEffect(() => {
    if (hasUnsavedChanges && config) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        performSave(config);
      }, 500);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config, hasUnsavedChanges, performSave]);

  if (!AuthService.userRoleIncludes(['admin'])) {
    return <Navigate to="/" replace={true} />;
  }

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  const airbnbSettingsContent = config && (
    <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Configuración de Airbnb</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
              Administra los datos requeridos del formulario y el reglamento usado para aceptación de huéspedes.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Documento de identificación</Typography>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_number?.ask)} onChange={(event) => updateConfig('airbnb_config.id_number.ask', event.target.checked)} color="secondary" />}
              label="Solicitar número de documento"
            />
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_number?.required)} onChange={(event) => updateConfig('airbnb_config.id_number.required', event.target.checked)} color="secondary" disabled={!config.airbnb_config?.id_number?.ask} />}
              label="Número de documento obligatorio"
            />
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Imagen del documento</Typography>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_picture?.ask)} onChange={(event) => updateConfig('airbnb_config.id_picture.ask', event.target.checked)} color="secondary" />}
              label="Solicitar imagen del documento"
            />
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.id_picture?.required)} onChange={(event) => updateConfig('airbnb_config.id_picture.required', event.target.checked)} color="secondary" disabled={!config.airbnb_config?.id_picture?.ask} />}
              label="Imagen del documento obligatoria"
            />
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Idiomas activos</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label="Español" color="secondary" variant="filled" />
              <Chip label="English" color="secondary" variant="outlined" />
            </Stack>
            <FormControlLabel
              control={<Switch checked={Boolean(config.airbnb_config?.languages?.includes('pt'))} onChange={(event) => setPortugueseEnabled(event.target.checked)} color="secondary" />}
              label="Habilitar portugués"
            />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'text.primary' }}>Ruleset para huéspedes</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                  Español es la fuente de verdad. Desde aquí puedes generar inglés y, si está habilitado, portugués.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="outlined" color="secondary" onClick={() => generateRuleset('missing')} disabled={rulesetBusy}>
                  {rulesetBusy ? 'Generando...' : 'Generar faltantes'}
                </Button>
                <Button variant="contained" color="secondary" onClick={() => generateRuleset('regenerate')} disabled={rulesetBusy}>
                  {rulesetBusy ? 'Generando...' : 'Regenerar idiomas'}
                </Button>
              </Stack>
            </Stack>

            <RulesEditor
              title="Español"
              value={config.ruleset?.es}
              onChange={(path, value) => updateRuleset('es', path, value)}
              onAddRule={() => addAirbnbRule('es')}
              onRemoveRule={(index) => removeAirbnbRule('es', index)}
            />

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: 'text.primary' }}>English</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{config.ruleset?.en?.title || 'Pendiente de generar'}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{Array.isArray(config.ruleset?.en?.rules) ? `${config.ruleset.en.rules.length} reglas` : 'Sin reglas'}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', opacity: config.airbnb_config?.languages?.includes('pt') ? 1 : 0.7 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 900, color: 'text.primary' }}>Português</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                        {config.airbnb_config?.languages?.includes('pt') ? (config.ruleset?.pt?.title || 'Pendiente de generar') : 'Deshabilitado'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {config.airbnb_config?.languages?.includes('pt')
                          ? (Array.isArray(config.ruleset?.pt?.rules) ? `${config.ruleset.pt.rules.length} reglas` : 'Sin reglas')
                          : 'Actívalo para generar y usar este idioma'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const visitSettingsContent = config && (
    <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Configuración de visitas</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
              Administra la operación de accesos, QR y reglas por defecto para la validez de visitas.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Tipos de acceso permitidos</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.walking)} onChange={(event) => updateConfig('rules.default.walking', event.target.checked)} color="secondary" />} label="Permitir acceso peatonal" />
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.vehicle)} onChange={(event) => updateConfig('rules.default.vehicle', event.target.checked)} color="secondary" />} label="Permitir acceso vehicular" />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Configuración QR</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.qr_confs?.qr_on_walking)} onChange={(event) => updateConfig('qr_confs.qr_on_walking', event.target.checked)} color="secondary" />} label="QR para acceso peatonal" />
            <FormControlLabel control={<Switch checked={Boolean(config.qr_confs?.qr_on_vehicle)} onChange={(event) => updateConfig('qr_confs.qr_on_vehicle', event.target.checked)} color="secondary" />} label="QR para acceso vehicular" />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Reglas de Check-in</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.checkin_rules?.require_id_image)} onChange={(event) => updateConfig('checkin_rules.require_id_image', event.target.checked)} color="secondary" />} label="Requerir imagen de identificación" />
            <FormControlLabel control={<Switch checked={Boolean(config.force_resident_vehicle_plate_input)} onChange={(event) => updateConfig('force_resident_vehicle_plate_input', event.target.checked)} color="secondary" />} label="Solicitar placa de vehículo de residente" />
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Validez por defecto (horas)"
                type="number"
                value={config.rules?.default?.default_lifespan_hours ?? 24}
                onChange={(event) => {
                  const nextValue = Math.max(1, Number(event.target.value) || 1);
                  updateConfig('rules.default.default_lifespan_hours', nextValue);
                }}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Validez máxima (días)"
                type="number"
                value={Math.max(1, Math.round((config.rules?.default?.max_lifespan_hours || 24) / 24))}
                onChange={(event) => {
                  const nextDays = Math.max(1, Number(event.target.value) || 1);
                  updateConfig('rules.default.max_lifespan_hours', nextDays * 24);
                }}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Opciones de duración</Typography>
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.allow_longer_lifespan)} onChange={(event) => updateConfig('rules.default.allow_longer_lifespan', event.target.checked)} color="secondary" />} label="Permitir ampliar la validez de la visita" />
            <FormControlLabel control={<Switch checked={Boolean(config.rules?.default?.allow_multiple_entries)} onChange={(event) => updateConfig('rules.default.allow_multiple_entries', event.target.checked)} color="secondary" />} label="Permitir múltiples entradas" />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Tipo de visita</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(config.visit_types_enabled)}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    if (enabled && (!Array.isArray(config.visit_types) || config.visit_types.length === 0)) {
                      updateConfig('visit_types', ['Visita Regular', 'Visita Proveedor']);
                    }
                    updateConfig('visit_types_enabled', enabled);
                  }}
                  color="secondary"
                />
              }
              label="Habilitar tipo de visita"
            />
            {Boolean(config.visit_types_enabled) && (
              <Box>
                <Stack spacing={1}>
                  {(config.visit_types || []).map((t, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        size="small"
                        value={t}
                        onChange={(event) => {
                          const next = [...(config.visit_types || [])];
                          next[idx] = event.target.value;
                          updateConfig('visit_types', next);
                        }}
                      />
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          const next = (config.visit_types || []).filter((_, i) => i !== idx);
                          updateConfig('visit_types', next);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Agregar tipo de visita"
                      value={visitTypeDraft}
                      onChange={(event) => setVisitTypeDraft(event.target.value)}
                    />
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}
                      onClick={() => {
                        const v = visitTypeDraft.trim();
                        if (!v) return;
                        const next = [...(config.visit_types || []), v];
                        updateConfig('visit_types', next);
                        setVisitTypeDraft('');
                      }}
                    >
                      Agregar
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const issuesSettingsContent = issuesConfig && (
    <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Configuración de Reporte de Averías</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
              Administra el prefijo de tickets, la privacidad y los estados del flujo de averías.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Prefijo de Ticket</Typography>
            <TextField
              label="Prefijo de Ticket de Avería"
              value={issuesConfig.issue_prefix || ''}
              onChange={(event) => updateIssuesConfig('issue_prefix', event.target.value)}
              inputProps={{ maxLength: 5 }}
              sx={{ maxWidth: 240 }}
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Privacidad de Tickets</Typography>
            <FormControlLabel
              control={<Switch checked={Boolean(issuesConfig.private_tickets)} onChange={(event) => updateIssuesConfig('private_tickets', event.target.checked)} color="secondary" />}
              label="Tickets privados (los residentes solo ven sus propios reportes)"
            />
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Cuando está activo, los residentes únicamente verán los reportes que ellos crearon. Administradores y recepción siempre ven todos los reportes.
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'text.primary' }}>Estados de Averías</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                  Define el nombre, orden y transiciones permitidas entre estados.
                </Typography>
              </Box>
              <Button variant="outlined" color="secondary" onClick={addIssueState} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Agregar estado
              </Button>
            </Stack>

            {Object.entries(issuesConfig.issues_states || {}).map(([stateKey, entry]) => {
              const allKeys = Object.keys(issuesConfig.issues_states || {});
              return (
                <Card key={stateKey} sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }}>
                      <TextField
                        label="Clave del estado"
                        value={stateKey}
                        onChange={(event) => renameIssueStateKey(stateKey, event.target.value)}
                        sx={{ maxWidth: 200 }}
                        size="small"
                      />
                      <Button color="error" size="small" onClick={() => removeIssueState(stateKey)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                        Eliminar
                      </Button>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Nombre corto" value={entry.nombre_corto || ''} onChange={(event) => updateIssueStateField(stateKey, 'nombre_corto', event.target.value)} size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Nombre de la acción" value={entry.nombre_accion || ''} onChange={(event) => updateIssueStateField(stateKey, 'nombre_accion', event.target.value)} size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label="Nombre largo" value={entry.nombre_largo || ''} onChange={(event) => updateIssueStateField(stateKey, 'nombre_largo', event.target.value)} size="small" fullWidth />
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', mb: 1 }}>Transiciones permitidas</Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {allKeys.map((targetKey) => {
                          if (targetKey === stateKey) { return null; }
                          const enabled = Array.isArray(entry.siguiente_estado) && entry.siguiente_estado.includes(targetKey);
                          return (
                            <Chip
                              key={targetKey}
                              label={issuesConfig.issues_states[targetKey]?.nombre_corto || targetKey}
                              color={enabled ? 'secondary' : 'default'}
                              variant={enabled ? 'filled' : 'outlined'}
                              clickable
                              onClick={() => toggleIssueStateTransition(stateKey, targetKey, !enabled)}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const gestionTypeCreatorOptions = React.useMemo(() => {
    const teamOptions = Object.entries(teams).filter(([key]) => !isReservedTeam(key)).map(([key, t]) => ({
      value: `admin:${key}`,
      label: `Admin: ${t?.nombre || key}`,
    }));
    return [...GESTION_TYPE_CREATOR_FIXED_OPTIONS, ...teamOptions];
  }, [teams]);

  const gestionTypeSettingsContent = gestionTypeDraft && (() => {
    const orgControlled = Array.isArray(gestionTypeDraft.org_controlled) ? gestionTypeDraft.org_controlled : [];
    const title = gestionTypeDraft.nombre_plural || gestionTypeDraft.nombre || gestionTypeDraft.key;
    return (
      <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>{title}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                Personaliza los nombres, prefijo de ticket y creadores permitidos para este tipo de gestión.
              </Typography>
            </Box>

            {orgControlled.includes('nombre') && (
              <TextField
                label={GESTION_TYPE_FIELD_LABELS.nombre}
                value={gestionTypeDraft.nombre || ''}
                onChange={(event) => updateGestionTypeDraft('nombre', event.target.value)}
                fullWidth
              />
            )}

            {orgControlled.includes('nombre_plural') && (
              <TextField
                label={GESTION_TYPE_FIELD_LABELS.nombre_plural}
                value={gestionTypeDraft.nombre_plural || ''}
                onChange={(event) => updateGestionTypeDraft('nombre_plural', event.target.value)}
                fullWidth
              />
            )}

            {orgControlled.includes('descripcion') && (
              <TextField
                label={GESTION_TYPE_FIELD_LABELS.descripcion}
                value={gestionTypeDraft.descripcion || ''}
                onChange={(event) => updateGestionTypeDraft('descripcion', event.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            )}

            {orgControlled.includes('id_prefix') && (
              <TextField
                label={GESTION_TYPE_FIELD_LABELS.id_prefix}
                value={gestionTypeDraft.id_prefix || ''}
                onChange={(event) => updateGestionTypeDraft('id_prefix', event.target.value)}
                inputProps={{ maxLength: 5 }}
                sx={{ maxWidth: 240 }}
              />
            )}

            {orgControlled.includes('creadores_permitidos') && (
              <FormControl fullWidth>
                <InputLabel id="gestion-type-creadores-label">{GESTION_TYPE_FIELD_LABELS.creadores_permitidos}</InputLabel>
                <Select
                  labelId="gestion-type-creadores-label"
                  multiple
                  value={Array.isArray(gestionTypeDraft.creadores_permitidos) ? gestionTypeDraft.creadores_permitidos : []}
                  label={GESTION_TYPE_FIELD_LABELS.creadores_permitidos}
                  onChange={(event) => updateGestionTypeDraft('creadores_permitidos', event.target.value)}
                  renderValue={(selected) => (Array.isArray(selected)
                    ? selected.map((val) => {
                        const opt = gestionTypeCreatorOptions.find((o) => o.value === val);
                        return opt ? opt.label : val;
                      }).join(', ')
                    : '')}
                >
                  {gestionTypeCreatorOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {orgControlled.includes('unassigned_rw_teams') && (
              <FormControl fullWidth>
                <InputLabel id="gestion-type-unassigned-rw-teams-label">{GESTION_TYPE_FIELD_LABELS.unassigned_rw_teams}</InputLabel>
                <Select
                  labelId="gestion-type-unassigned-rw-teams-label"
                  multiple
                  value={Array.isArray(gestionTypeDraft.unassigned_rw_teams) ? gestionTypeDraft.unassigned_rw_teams : []}
                  label={GESTION_TYPE_FIELD_LABELS.unassigned_rw_teams}
                  onChange={(event) => updateGestionTypeDraft('unassigned_rw_teams', event.target.value)}
                  renderValue={(selected) => (Array.isArray(selected)
                    ? selected.map((key) => teams[key]?.nombre || key).join(', ')
                    : '')}
                >
                  {Object.entries(teams).filter(([key]) => !isReservedTeam(key)).map(([key, t]) => (
                    <MenuItem key={key} value={key}>{t?.nombre || key}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button variant="outlined" color="secondary" onClick={handleCloseGestionTypeSettings} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Volver
              </Button>
              <Button variant="contained" color="secondary" onClick={performGestionTypeSave} disabled={gestionTypeSaving} sx={{ textTransform: 'none', fontWeight: 800 }}>
                {gestionTypeSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  })();

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }} data-tour="admin-dashboard-screen">
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 3,
            pb: 2,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ overflow: 'hidden' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <IconButton
                onClick={settingsScreen === 'airbnb' ? handleCloseAirbnbSettings : settingsScreen === 'visits' ? handleCloseVisitSettings : settingsScreen === 'issues' ? handleCloseIssuesSettings : (typeof settingsScreen === 'string' && settingsScreen.startsWith('gestion_type_')) ? handleCloseGestionTypeSettings : () => navigate(-1)}
                sx={{
                  width: 40,
                  height: 40,
                  ml: -1,
                  color: 'text.primary',
                  '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <SolarIcon name="settings" size={26} color="primary.main" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {settingsScreen ? 'Módulos' : 'Configuración administrativa'}
                </Typography>
                <Typography noWrap sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em', fontFamily: '"Nexa", "Roboto", "Helvetica", "Arial", sans-serif' }}>
                  {settingsScreen === 'airbnb' ? 'Configuración Airbnb' : settingsScreen === 'visits' ? 'Configuración de visitas' : settingsScreen === 'issues' ? 'Configuración de Averías' : (typeof settingsScreen === 'string' && settingsScreen.startsWith('gestion_type_')) ? 'Configuración de Gestión' : 'Dashboard'}
                </Typography>
              </Box>
            </Stack>
            {planChipLabel && <Chip color="secondary" label={planChipLabel} sx={{ flexShrink: 0, maxWidth: { xs: 130, sm: 'none' }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} />}
          </Stack>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {loading && (
            <Card>
              <CardContent sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress color="secondary" />
                <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>Cargando configuración...</Typography>
              </CardContent>
            </Card>
          )}

          {!loading && error && !config && <Alert severity="error">{error}</Alert>}

          {!loading && config && (
            <>
              {settingsScreen === 'airbnb' ? (
                airbnbSettingsContent
              ) : settingsScreen === 'visits' ? (
                visitSettingsContent
              ) : settingsScreen === 'issues' ? (
                issuesConfigLoading && !issuesConfig ? (
                  <Card><CardContent sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}><CircularProgress color="secondary" /><Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>Cargando configuración de averías...</Typography></CardContent></Card>
                ) : (
                  issuesSettingsContent
                )
              ) : (typeof settingsScreen === 'string' && settingsScreen.startsWith('gestion_type_')) ? (
                gestionTypeSettingsContent
              ) : (
                <>
                  <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Tabs
                        value={activeTab}
                        onChange={(_, nextValue) => setActiveTab(nextValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                      >
                        {TABS.map((tab) => (
                          <Tab key={tab.label} icon={tab.icon} iconPosition="start" label={tab.label} sx={{ textTransform: 'none', fontWeight: 800 }} />
                        ))}
                      </Tabs>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {activeTab === 0 && (
                        <Stack spacing={3}>
                          <FormControl fullWidth>
                            <InputLabel id="notification-mode-label">Notificaciones de Reservaciones</InputLabel>
                            <Select labelId="notification-mode-label" label="Notificaciones de Reservaciones" value={config.reservation_notification_mode || 'push'} onChange={(event) => updateConfig('reservation_notification_mode', event.target.value)}>
                              {NOTIFICATION_MODES.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Divider />
                          <MailingListField label="Emails de Reportería" list={config.reporting_email_list || []} onAdd={(value) => addToList('reporting_email_list', value)} onRemove={(index) => removeFromList('reporting_email_list', index)} />
                          <MailingListField label="Lista de Correo de Averías" list={config.issues_mailing_list || []} onAdd={(value) => addToList('issues_mailing_list', value)} onRemove={(index) => removeFromList('issues_mailing_list', index)} />
                          <MailingListField label="Lista de Correo de Reservaciones" list={config.reservation_mailing_list || []} onAdd={(value) => addToList('reservation_mailing_list', value)} onRemove={(index) => removeFromList('reservation_mailing_list', index)} />
                        </Stack>
                      )}

                      {activeTab === 1 && (
                        <Stack spacing={3}>
                          <Grid container spacing={2}>
                            {ALL_MODULES.map((module) => {
                              const isActive = (config.modules || []).includes(module.key);
                              return (
                                <Grid item xs={12} sm={6} key={module.key}>
                                  <Card
                                    onClick={() => {
                                      const currentModules = Array.isArray(config.modules) ? config.modules : [];
                                      const nextModules = isActive ? currentModules.filter((item) => item !== module.key) : [...currentModules, module.key];
                                      updateConfig('modules', nextModules);
                                    }}
                                    sx={{
                                      borderRadius: '10px',
                                      boxShadow: 0,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      backgroundColor: isActive ? (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08) : 'background.paper',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                                        <Box sx={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary' }}>
                                          <SolarIcon name={module.icon} size={28} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary' }}>{module.label}</Typography>
                                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{isActive ? 'Activo' : 'Inactivo'}</Typography>
                                          {module.key === 'airbnb' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenAirbnbSettings();
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                          {module.key === 'visits' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenVisitSettings();
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                          {module.key === 'issues' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenIssuesSettings();
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                          {module.key === 'amenities' && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                navigate('/amenityadmin');
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                        </Box>
                                      </Stack>
                                      <Switch checked={isActive} color="secondary" />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                            {gestionTypes.map((gtype) => {
                              const moduleKey = `gestiones:${gtype.key}`;
                              const isActive = (config.modules || []).includes(moduleKey);
                              const label = gtype.nombre_plural || gtype.nombre || gtype.key;
                              return (
                                <Grid item xs={12} sm={6} key={moduleKey}>
                                  <Card
                                    onClick={() => {
                                      const currentModules = Array.isArray(config.modules) ? config.modules : [];
                                      const nextModules = isActive ? currentModules.filter((item) => item !== moduleKey) : [...currentModules, moduleKey];
                                      updateConfig('modules', nextModules);
                                    }}
                                    sx={{
                                      borderRadius: '10px',
                                      boxShadow: 0,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      backgroundColor: isActive ? (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08) : 'background.paper',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                                        <Box sx={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary' }}>
                                          <GestionTypeIcon typeKey={gtype.key} size={28} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary' }}>{label}</Typography>
                                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{isActive ? 'Activo' : 'Inactivo'}</Typography>
                                          {Array.isArray(gtype.org_controlled) && gtype.org_controlled.length > 0 && (
                                            <Button
                                              size="small"
                                              color="secondary"
                                              sx={{ px: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 800 }}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenGestionTypeSettings(gtype.key);
                                              }}
                                            >
                                              Configurar
                                            </Button>
                                          )}
                                        </Box>
                                      </Stack>
                                      <Switch checked={isActive} color="secondary" />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      )}

                      {activeTab === 2 && (
                        <Stack spacing={3}>
                          <TextField
                            label="Dirección completa"
                            value={config.full_address || ''}
                            onChange={(event) => updateConfig('full_address', event.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Ej: Calle Principal 123, Zona 10, Ciudad de Guatemala"
                          />
                          <LocationPickerMap
                            value={config.location || null}
                            onChange={(next) => updateConfig('location', next)}
                          />
                          <Stack spacing={1.5}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>Logo de la Organización</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                              {orgKey && (
                                <Box
                                  component="img"
                                  src={`${orgLogoUrl(orgKey, 'original')}?v=${logoVersion}`}
                                  alt="Logo actual"
                                  sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
                                />
                              )}
                              {logoPreview && (
                                <Box
                                  component="img"
                                  src={logoPreview}
                                  alt="Nuevo logo"
                                  sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'secondary.main' }}
                                />
                              )}
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Button variant="outlined" color="secondary" component="label" sx={{ textTransform: 'none', fontWeight: 700 }}>
                                  Seleccionar
                                  <Box component="input" type="file" accept="image/*" hidden onChange={handleLogoChange} />
                                </Button>
                                {logoFile && (
                                  <Button variant="contained" color="secondary" onClick={handleLogoUpload} disabled={logoProcessing || logoUploading} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                    {logoUploading ? 'Subiendo...' : 'Guardar'}
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                            {logoProcessing && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} color="secondary" />
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Analizando contraste...</Typography>
                              </Stack>
                            )}
                            {logoAnalysis && !logoProcessing && (logoAnalysis.conflictsLight || logoAnalysis.conflictsDark) && (
                              <Alert severity="warning" sx={{ fontSize: 12 }}>
                                Se detectaron problemas de contraste. Se generarán variantes automáticamente para modo {logoAnalysis.conflictsLight ? 'claro' : ''}{logoAnalysis.conflictsLight && logoAnalysis.conflictsDark ? ' y ' : ''}{logoAnalysis.conflictsDark ? 'oscuro' : ''}.
                              </Alert>
                            )}
                            {logoAnalysis && !logoProcessing && !logoAnalysis.conflictsLight && !logoAnalysis.conflictsDark && (
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>El logo tiene buen contraste en ambos modos.</Typography>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField label="Color de la Organización" value={config.color || ''} onChange={(event) => updateConfig('color', event.target.value)} sx={{ flex: 1 }} />
                            <Box component="input" type="color" value={config.color || '#238048'} onChange={(event) => updateConfig('color', event.target.value)} sx={{ width: 56, height: 56, border: 'none', background: 'transparent', p: 0, cursor: 'pointer', borderRadius: '50%', overflow: 'hidden', '&::-webkit-color-swatch-wrapper': { padding: 0, borderRadius: '50%' }, '&::-webkit-color-swatch': { border: 'none', borderRadius: '50%' }, '&::-moz-color-swatch': { border: 'none', borderRadius: '50%' } }} />
                          </Stack>
                          <FormControl fullWidth>
                            <InputLabel id="unit-type-label">Tipo de Unidad</InputLabel>
                            <Select
                              labelId="unit-type-label"
                              label="Tipo de Unidad"
                              value={config.type || ''}
                              onChange={(event) => updateConfig('type', event.target.value)}
                            >
                              {UNIT_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Divider />
                          <Stack spacing={1.5}>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="secondary"
                                  checked={Boolean(config.multi_tower?.enabled)}
                                  onChange={(event) => updateConfig('multi_tower', {
                                    enabled: event.target.checked,
                                    towers: Array.isArray(config.multi_tower?.towers) ? config.multi_tower.towers : [],
                                  })}
                                />
                              }
                              label="Esquema multi-torre"
                            />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                              Las torres son prefijos literales que coinciden con el string de la unidad (ej. "T1" en "T1-704"). Los nombres de torre no pueden contener guiones, espacios, ni estar vacíos.
                            </Typography>
                            {config.multi_tower?.enabled && (
                              <>
                                <Alert severity="warning" sx={{ fontSize: 12 }}>
                                  Las unidades existentes no serán renombradas — reconcilia manualmente.
                                </Alert>
                                {(config.multi_tower?.towers || []).length === 0 && (
                                  <Alert severity="info" sx={{ fontSize: 12 }}>
                                    Agregue al menos una torre para guardar este esquema.
                                  </Alert>
                                )}
                                <TowerListField
                                  list={config.multi_tower?.towers || []}
                                  onAdd={(name) => updateConfig('multi_tower', {
                                    enabled: true,
                                    towers: [...(config.multi_tower?.towers || []), name],
                                  })}
                                  onRemove={(index) => updateConfig('multi_tower', {
                                    enabled: true,
                                    towers: (config.multi_tower?.towers || []).filter((_, i) => i !== index),
                                  })}
                                />
                              </>
                            )}
                          </Stack>
                        </Stack>
                      )}

                      {activeTab === 3 && (
                        <Stack spacing={3}>
                          <Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>Equipos &amp; Gestiones</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>
                              Define qué equipos pueden ver (R) o editar (R/W) cada tipo de gestión. Haz clic en una celda para ciclar: ninguno → lectura → lectura/escritura → ninguno. Las celdas vacías usan el valor por defecto del tipo de gestión.
                            </Typography>
                          </Box>

                          {rbacLoading && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CircularProgress size={16} color="secondary" />
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Cargando matriz de permisos...</Typography>
                            </Stack>
                          )}

                          {!rbacLoading && rbacTeams.length === 0 && (
                            <Alert severity="info">No hay equipos configurados. Crea equipos desde la pantalla de administración de usuarios para definir permisos por equipo.</Alert>
                          )}

                          {!rbacLoading && rbacTeams.length > 0 && rbacTypes.length === 0 && (
                            <Alert severity="info">No hay tipos de gestión habilitados. Habilita un tipo de gestión desde la pestaña Módulos para configurar sus permisos.</Alert>
                          )}

                          {!rbacLoading && rbacTeams.length > 0 && rbacTypes.length > 0 && (
                            <>
                              <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 320 }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.06) }}>
                                      <TableCell sx={{ fontWeight: 900, color: 'text.primary', whiteSpace: 'nowrap' }}>Tipo de Gestión</TableCell>
                                      {rbacTeams.map((team) => (
                                        <TableCell key={team.key} align="center" sx={{ fontWeight: 900, color: 'text.primary', whiteSpace: 'nowrap' }}>
                                          {team.nombre}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {rbacTypes.map((type) => (
                                      <TableRow key={type.key} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap' }}>{type.nombre}</TableCell>
                                        {rbacTeams.map((team) => {
                                          const state = rbacCellState(rbacMatrix, type.key, team.key);
                                          const chipLabel = state === 'none' ? '—' : state === 'read' ? 'R' : 'R/W';
                                          const chipColor = state === 'write' ? 'secondary' : state === 'read' ? 'secondary' : 'default';
                                          const chipVariant = state === 'none' ? 'outlined' : 'filled';
                                          return (
                                            <TableCell key={team.key} align="center" sx={{ py: 1 }}>
                                              <Chip
                                                label={chipLabel}
                                                color={chipColor}
                                                variant={chipVariant}
                                                clickable
                                                onClick={() => cycleRbacCell(type.key, team.key)}
                                                sx={{ minWidth: 44, fontWeight: 800 }}
                                              />
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => performRbacSave(rbacMatrix)}
                                  disabled={!rbacDirty || rbacSaving}
                                  sx={{ textTransform: 'none', fontWeight: 800 }}
                                >
                                  {rbacSaving ? 'Guardando...' : 'Guardar matriz'}
                                </Button>
                                {rbacDirty && !rbacSaving && (
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Hay cambios sin guardar.</Typography>
                                )}
                              </Stack>
                            </>
                          )}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {error && !config && <Alert severity="error">{error}</Alert>}
            </>
          )}
        </Box>

        <CustomSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </Box>
    </Container>
  );
}
